import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import ActivityPanel from '../components/ActivityPanel';

const SUPPLIERS = [
  'Microchip', 'Everspin', 'Grayhill', 'Harwin', 'Bel Fuse', 'Bel Power',
  'CUI', 'East Key', 'Qualastat', 'EZ Form', 'PhD Energy', '28 Gorilla', 'Federal Electronics',
  'Miscellaneous'
];

const STAGES = ['prospecting','qualified','proposal','negotiation','closed_won','closed_lost'];

const fmt = n => Number(n || 0).toLocaleString('en-US', { style:'currency', currency:'USD', maximumFractionDigits:2 });

export default function OpportunityDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [deal, setDeal] = useState(null);
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [distContacts, setDistContacts] = useState([]);

  // Line item form
  const [showLineForm, setShowLineForm] = useState(false);
  const [lineForm, setLineForm] = useState({ supplier:'', part_number:'', sale_price:'', quantity:1 });
  const [savingLine, setSavingLine] = useState(false);
  const [lineError, setLineError] = useState('');

  // Distributor panel
  const [distForm, setDistForm] = useState({ distributor_id:'', distributor_contact_id:'' });
  const [savingDist, setSavingDist] = useState(false);
  const [distEditing, setDistEditing] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/deals/${id}`)
      .then(r => {
        setDeal(r.data.deal);
        setForm(r.data.deal);
        setLines(r.data.lines || []);
        setDistForm({
          distributor_id: r.data.deal.distributor_id || '',
          distributor_contact_id: r.data.deal.distributor_contact_id || '',
        });
        if (r.data.deal.distributor_id) {
          loadDistContacts(r.data.deal.distributor_id);
        }
      })
      .catch(() => navigate('/pipeline'))
      .finally(() => setLoading(false));
  };

  const loadDistContacts = (accountId) => {
    if (!accountId) { setDistContacts([]); return; }
    api.get(`/accounts/${accountId}`)
      .then(r => setDistContacts(r.data.contacts || []))
      .catch(() => {});
  };

  useEffect(() => {
    load();
    api.get('/users').then(r => setUsers(r.data.users || [])).catch(() => {});
    api.get('/accounts?limit=100').then(r => setAccounts(r.data.accounts || [])).catch(() => {});
    api.get('/contacts?limit=200').then(r => setContacts(r.data.contacts || [])).catch(() => {});
  }, [id]);

  const isClosed = deal && ['closed_won','closed_lost'].includes(deal.stage);

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/deals/${id}`, {
        ...form,
        company_id: form.company_id || null,
        contact_id: form.contact_id || null,
        owner_id: form.owner_id || null,
      });
      setDeal(r.data.deal);
      setEditing(false);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDist = async () => {
    setSavingDist(true);
    try {
      const r = await api.patch(`/deals/${id}`, {
        distributor_id: distForm.distributor_id || null,
        distributor_contact_id: distForm.distributor_contact_id || null,
      });
      setDeal(r.data.deal);
      setDistEditing(false);
    } catch (err) {
      alert('Failed to save distributor info.');
    } finally {
      setSavingDist(false);
    }
  };

  const handleDesignRegistered = async (value) => {
    await api.patch(`/deals/${id}`, { design_registered: value });
    setDeal(prev => ({ ...prev, design_registered: value }));
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this opportunity? This cannot be undone.')) return;
    await api.delete(`/deals/${id}`);
    navigate('/pipeline');
  };

  const handleAddLine = async () => {
    setLineError('');
    if (!lineForm.supplier) return setLineError('Supplier is required.');
    if (!lineForm.sale_price || isNaN(lineForm.sale_price)) return setLineError('Valid sale price is required.');
    if (!lineForm.quantity || lineForm.quantity < 1) return setLineError('Quantity must be at least 1.');
    setSavingLine(true);
    try {
      await api.post(`/deals/${id}/lines`, {
        manufacturer: lineForm.supplier,
        part_number: lineForm.part_number,
        sale_price: lineForm.sale_price,
        quantity: lineForm.quantity,
      });
      setLineForm({ supplier:'', part_number:'', sale_price:'', quantity:1 });
      setShowLineForm(false);
      load();
    } catch (err) {
      setLineError(err.response?.data?.error || 'Failed to add line item.');
    } finally {
      setSavingLine(false);
    }
  };

  const handleRemoveLine = async (lineId) => {
    if (!window.confirm('Remove this line item?')) return;
    try {
      await api.delete(`/deals/${id}/lines/${lineId}`);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to remove line item.');
    }
  };

  const stageBadge = s => {
    const map = { prospecting:'badge-gray', qualified:'badge-blue', proposal:'badge-amber',
                  negotiation:'badge-red', closed_won:'badge-green', closed_lost:'badge-gray' };
    return <span className={`badge ${map[s]||'badge-gray'}`}>{s.replace('_',' ')}</span>;
  };

  if (loading) return <div style={{ fontSize:13, color:'var(--text-tertiary)' }}>Loading…</div>;
  if (!deal) return null;

  const lineTotal = lines.reduce((s, l) => s + parseFloat(l.total_price || 0), 0);

  return (
    <>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
        <button className="btn" onClick={() => navigate('/pipeline')}>← Back</button>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:20, fontWeight:600 }}>{deal.name}</div>
          <div style={{ fontSize:12, color:'var(--text-secondary)', marginTop:2, display:'flex', gap:8, alignItems:'center' }}>
            {stageBadge(deal.stage)}
            <span>Created {new Date(deal.created_at).toLocaleDateString()}</span>
            {deal.owner_name && <span>· Owner: {deal.owner_name}</span>}
            {deal.design_registered && <span className="badge badge-green">Design Registered</span>}
          </div>
        </div>
        {!isClosed && <button className="btn" onClick={() => setEditing(!editing)}>{editing ? 'Cancel' : 'Edit'}</button>}
        <button className="btn" onClick={handleDelete} style={{ color:'var(--danger)' }}>Delete</button>
      </div>

      {/* Edit Form */}
      {editing && (
        <div className="card" style={{ marginBottom:16 }}>
          <div className="section-title" style={{ marginBottom:14 }}>Edit opportunity</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
            <div>
              <label className="form-label">Opportunity name *</label>
              <input className="form-input" value={form.name||''}
                onChange={e => setForm({...form, name:e.target.value})} />
            </div>
            <div>
              <label className="form-label">Stage</label>
              <select className="form-input" value={form.stage||'prospecting'}
                onChange={e => setForm({...form, stage:e.target.value})}>
                {STAGES.map(s => <option key={s} value={s}>{s.replace('_',' ')}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Close date</label>
              <input className="form-input" type="date" value={form.close_date?.split('T')[0]||''}
                onChange={e => setForm({...form, close_date:e.target.value})} />
            </div>
            <div>
              <label className="form-label">Account</label>
              <select className="form-input" value={form.company_id||''}
                onChange={e => setForm({...form, company_id:e.target.value})}>
                <option value="">— No account —</option>
                {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Contact</label>
              <select className="form-input" value={form.contact_id||''}
                onChange={e => setForm({...form, contact_id:e.target.value})}>
                <option value="">— No contact —</option>
                {contacts.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Owner</label>
              <select className="form-input" value={form.owner_id||''}
                onChange={e => setForm({...form, owner_id:e.target.value})}>
                <option value="">— Unassigned —</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
              </select>
            </div>
            <div style={{ gridColumn:'span 3' }}>
              <label className="form-label">Notes</label>
              <input className="form-input" value={form.notes||''}
                onChange={e => setForm({...form, notes:e.target.value})}
                placeholder="Optional notes" />
            </div>
          </div>
          <div style={{ display:'flex', gap:8, marginTop:14 }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
            <button className="btn" onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Main layout */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 340px', gap:16, alignItems:'start' }}>

        {/* LEFT SIDE */}
        <div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>

            {/* Opportunity details */}
            <div className="card">
              <div className="section-title">Opportunity details</div>
              <table className="data-table">
                <tbody>
                  <tr><td style={{ color:'var(--text-secondary)', width:120 }}>Account</td><td>{deal.company_name || '—'}</td></tr>
                  <tr><td style={{ color:'var(--text-secondary)' }}>Contact</td><td>{deal.contact_name || '—'}</td></tr>
                  <tr><td style={{ color:'var(--text-secondary)' }}>Owner</td><td>{deal.owner_name || 'Unassigned'}</td></tr>
                  <tr><td style={{ color:'var(--text-secondary)' }}>Stage</td><td>{stageBadge(deal.stage)}</td></tr>
                  <tr><td style={{ color:'var(--text-secondary)' }}>Close date</td><td>{deal.close_date ? new Date(deal.close_date).toLocaleDateString() : '—'}</td></tr>
                  <tr><td style={{ color:'var(--text-secondary)' }}>Created</td><td>{new Date(deal.created_at).toLocaleDateString()}</td></tr>
                  {deal.notes && <tr><td style={{ color:'var(--text-secondary)' }}>Notes</td><td>{deal.notes}</td></tr>}
                  <tr>
                    <td style={{ color:'var(--text-secondary)' }}>Design registered</td>
                    <td>
                      <div style={{ display:'flex', gap:8 }}>
                        <button onClick={() => handleDesignRegistered(true)}
                          style={{ height:26, padding:'0 12px', fontSize:12, cursor:'pointer',
                                   borderRadius:'var(--radius)', border:'0.5px solid var(--border-md)',
                                   background: deal.design_registered === true ? '#EAF3DE' : 'var(--bg-secondary)',
                                   color: deal.design_registered === true ? '#27500A' : 'var(--text-secondary)',
                                   fontWeight: deal.design_registered === true ? 600 : 400 }}>Yes</button>
                        <button onClick={() => handleDesignRegistered(false)}
                          style={{ height:26, padding:'0 12px', fontSize:12, cursor:'pointer',
                                   borderRadius:'var(--radius)', border:'0.5px solid var(--border-md)',
                                   background: deal.design_registered === false ? '#FCEBEB' : 'var(--bg-secondary)',
                                   color: deal.design_registered === false ? '#791F1F' : 'var(--text-secondary)',
                                   fontWeight: deal.design_registered === false ? 600 : 400 }}>No</button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
              {/* Financial summary */}
              <div className="card">
                <div className="section-title">Financial summary</div>
                <table className="data-table">
                  <tbody>
                    <tr><td style={{ color:'var(--text-secondary)', width:140 }}>Line items total</td>
                      <td style={{ fontWeight:600, fontSize:16, color:'var(--accent-dark)' }}>{fmt(lineTotal)}</td></tr>
                    <tr><td style={{ color:'var(--text-secondary)' }}>Opportunity amount</td>
                      <td style={{ fontWeight:500 }}>{fmt(deal.amount)}</td></tr>
                    <tr><td style={{ color:'var(--text-secondary)' }}>Line items</td>
                      <td>{lines.length}</td></tr>
                  </tbody>
                </table>
              </div>

              {/* Distribution partner panel */}
              <div className="card">
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
                  <div className="section-title" style={{ marginBottom:0 }}>Distribution partner</div>
                  <button className="btn" style={{ fontSize:12, height:28 }}
                    onClick={() => setDistEditing(!distEditing)}>
                    {distEditing ? 'Cancel' : 'Edit'}
                  </button>
                </div>
                {distEditing ? (
                  <div>
                    <div style={{ marginBottom:10 }}>
                      <label className="form-label">Distributor account</label>
                      <select className="form-input" value={distForm.distributor_id}
                        onChange={e => {
                          setDistForm({...distForm, distributor_id: e.target.value, distributor_contact_id:''});
                          loadDistContacts(e.target.value);
                        }}>
                        <option value="">— None —</option>
                        {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                      </select>
                    </div>
                    <div style={{ marginBottom:10 }}>
                      <label className="form-label">Distributor contact</label>
                      <select className="form-input" value={distForm.distributor_contact_id}
                        onChange={e => setDistForm({...distForm, distributor_contact_id: e.target.value})}
                        disabled={!distForm.distributor_id}>
                        <option value="">— None —</option>
                        {distContacts.map(c => (
                          <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>
                        ))}
                      </select>
                    </div>
                    <div style={{ display:'flex', gap:8 }}>
                      <button className="btn btn-primary" style={{ fontSize:12, height:28 }}
                        onClick={handleSaveDist} disabled={savingDist}>
                        {savingDist ? 'Saving…' : 'Save'}
                      </button>
                      <button className="btn" style={{ fontSize:12, height:28 }}
                        onClick={() => setDistEditing(false)}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <table className="data-table">
                    <tbody>
                      <tr><td style={{ color:'var(--text-secondary)', width:100 }}>Distributor</td>
                        <td>{deal.distributor_name || <span style={{ color:'var(--text-tertiary)', fontStyle:'italic' }}>Not set</span>}</td>
                      </tr>
                      <tr><td style={{ color:'var(--text-secondary)' }}>Contact</td>
                        <td>{deal.distributor_contact_name || <span style={{ color:'var(--text-tertiary)', fontStyle:'italic' }}>Not set</span>}</td>
                      </tr>
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>

          {/* Line Items */}
          <div className="card">
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
              <div className="section-title" style={{ marginBottom:0 }}>
                Products / Line items ({lines.length})
              </div>
              {!isClosed && (
                <button className="btn btn-primary" style={{ fontSize:12, height:28 }}
                  onClick={() => { setShowLineForm(!showLineForm); setLineError(''); }}>
                  {showLineForm ? 'Cancel' : '+ Add line item'}
                </button>
              )}
              {isClosed && (
                <span style={{ fontSize:12, color:'var(--text-tertiary)' }}>
                  Opportunity is closed — line items are locked
                </span>
              )}
            </div>

            {showLineForm && (
              <div style={{ background:'var(--bg-secondary)', borderRadius:'var(--radius)', padding:14, marginBottom:14 }}>
                <div style={{ display:'grid', gridTemplateColumns:'2fr 2fr 1fr 1fr', gap:10, marginBottom:10 }}>
                  <div>
                    <label className="form-label">Supplier *</label>
                    <select className="form-input" value={lineForm.supplier}
                      onChange={e => setLineForm({...lineForm, supplier:e.target.value})}>
                      <option value="">— Select supplier —</option>
                      {SUPPLIERS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="form-label">Supplier part number</label>
                    <input className="form-input" value={lineForm.part_number}
                      onChange={e => setLineForm({...lineForm, part_number:e.target.value})}
                      placeholder="e.g. M22-2530605" />
                  </div>
                  <div>
                    <label className="form-label">Sale price ($) *</label>
                    <input className="form-input" type="number" step="0.01" value={lineForm.sale_price}
                      onChange={e => setLineForm({...lineForm, sale_price:e.target.value})}
                      placeholder="0.00" />
                  </div>
                  <div>
                    <label className="form-label">Quantity *</label>
                    <input className="form-input" type="number" min="1" value={lineForm.quantity}
                      onChange={e => setLineForm({...lineForm, quantity:parseInt(e.target.value)||1})}
                      placeholder="1" />
                  </div>
                </div>
                {lineForm.sale_price && lineForm.quantity && (
                  <div style={{ fontSize:13, color:'var(--text-secondary)', marginBottom:8 }}>
                    Line total: <strong>{fmt(parseFloat(lineForm.sale_price||0) * parseInt(lineForm.quantity||1))}</strong>
                  </div>
                )}
                {lineError && <div className="form-error" style={{ marginBottom:8 }}>{lineError}</div>}
                <div style={{ display:'flex', gap:8 }}>
                  <button className="btn btn-primary" onClick={handleAddLine} disabled={savingLine}>
                    {savingLine ? 'Adding…' : 'Add line item'}
                  </button>
                  <button className="btn" onClick={() => { setShowLineForm(false); setLineError(''); }}>Cancel</button>
                </div>
              </div>
            )}

            {lines.length === 0
              ? <p style={{ fontSize:13, color:'var(--text-tertiary)' }}>
                  {isClosed ? 'No line items.' : 'No line items yet. Click "+ Add line item" to add products.'}
                </p>
              : <>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Supplier</th><th>Supplier part number</th>
                        <th>Sale price</th><th>Quantity</th><th>Total price</th>
                        {!isClosed && <th></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map(l => (
                        <tr key={l.id}>
                          <td style={{ fontWeight:500 }}>{l.manufacturer}</td>
                          <td style={{ color:'var(--text-secondary)', fontFamily:'monospace', fontSize:12 }}>{l.part_number || '—'}</td>
                          <td>{fmt(l.sale_price)}</td>
                          <td>{l.quantity.toLocaleString()}</td>
                          <td style={{ fontWeight:500, color:'var(--accent-dark)' }}>{fmt(l.total_price)}</td>
                          {!isClosed && (
                            <td>
                              <button onClick={() => handleRemoveLine(l.id)}
                                style={{ background:'none', border:'none', cursor:'pointer', color:'var(--danger)', fontSize:12 }}>
                                Remove
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={4} style={{ textAlign:'right', fontWeight:600, padding:'10px 12px', color:'var(--text-secondary)' }}>Total</td>
                        <td style={{ fontWeight:700, color:'var(--accent-dark)', padding:'10px 12px', fontSize:15 }}>{fmt(lineTotal)}</td>
                        {!isClosed && <td></td>}
                      </tr>
                    </tfoot>
                  </table>
                </>
            }
          </div>
        </div>

        {/* RIGHT SIDE — Activity Panel */}
        <div className="card" style={{ position:'sticky', top:20 }}>
          <div className="section-title" style={{ marginBottom:12 }}>Activity</div>
          <ActivityPanel dealId={id} />
        </div>

      </div>
    </>
  );
}